import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Unsubscribe,
  Timestamp,
  QueryDocumentSnapshot,
  startAfter,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Notification,
  NotificationType,
  NotificationPriority,
} from '@/types';
import { AuthService } from './auth';
import { Permission } from '@/types';

export class NotificationService {
  private static collection = 'notifications';

  // Create a new notification
  static async createNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    priority: NotificationPriority = NotificationPriority.MEDIUM,
    data?: Record<string, any>
  ): Promise<Notification> {
    try {
      const notificationData = {
        userId,
        type,
        title,
        message,
        priority,
        data: data || {},
        read: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        status: 'active' as const,
      };

      const docRef = await addDoc(collection(db, this.collection), notificationData);
      return {
        id: docRef.id,
        ...notificationData,
      } as Notification;
    } catch (error: any) {
      throw new Error(`Failed to create notification: ${error.message || error}`);
    }
  }

  // Get notifications for a user
  static async getNotifications(
    userId: string,
    options: {
      read?: boolean;
      type?: NotificationType;
      limit?: number;
      lastDoc?: QueryDocumentSnapshot;
    } = {}
  ): Promise<{ notifications: Notification[]; lastDoc: QueryDocumentSnapshot | null; hasMore: boolean }> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to get notifications');
      }

      // Users can only get their own notifications
      if (currentUser.id !== userId && !AuthService.hasPermission(currentUser, Permission.MANAGE_SYSTEM)) {
        throw new Error('You can only view your own notifications');
      }

      let q = query(
        collection(db, this.collection),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      // Filter by read status
      if (options.read !== undefined) {
        q = query(q, where('read', '==', options.read));
      }

      // Filter by type
      if (options.type) {
        q = query(q, where('type', '==', options.type));
      }

      // Apply pagination
      const pageLimit = options.limit || 20;
      if (options.lastDoc) {
        q = query(q, startAfter(options.lastDoc), limit(pageLimit + 1));
      } else {
        q = query(q, limit(pageLimit + 1));
      }

      const querySnapshot = await getDocs(q);
      const notifications = querySnapshot.docs.slice(0, pageLimit).map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Notification[];

      const hasMore = querySnapshot.docs.length > pageLimit;
      const lastDoc = hasMore ? querySnapshot.docs[pageLimit - 1] : null;

      return {
        notifications,
        lastDoc,
        hasMore,
      };
    } catch (error: any) {
      throw new Error(`Failed to get notifications: ${error.message || error}`);
    }
  }

  // Mark notification as read
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to mark notifications as read');
      }

      const notificationRef = doc(db, this.collection, notificationId);
      const notificationSnap = await getDoc(notificationRef);

      if (!notificationSnap.exists()) {
        throw new Error('Notification not found');
      }

      const notification = notificationSnap.data() as Notification;

      // Users can only mark their own notifications as read
      if (notification.userId !== currentUser.id && !AuthService.hasPermission(currentUser, Permission.MANAGE_SYSTEM)) {
        throw new Error('You can only mark your own notifications as read');
      }

      await updateDoc(notificationRef, {
        read: true,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to mark notification as read: ${error.message || error}`);
    }
  }

  // Mark multiple notifications as read
  static async markAllAsRead(userId: string): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to mark notifications as read');
      }

      // Users can only mark their own notifications as read
      if (currentUser.id !== userId && !AuthService.hasPermission(currentUser, Permission.MANAGE_SYSTEM)) {
        throw new Error('You can only mark your own notifications as read');
      }

      const q = query(
        collection(db, this.collection),
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const updatePromises = querySnapshot.docs.map((doc) =>
        updateDoc(doc.ref, {
          read: true,
          updatedAt: Timestamp.now(),
        })
      );

      await Promise.all(updatePromises);
    } catch (error: any) {
      throw new Error(`Failed to mark all notifications as read: ${error.message || error}`);
    }
  }

  // Delete notification
  static async deleteNotification(notificationId: string): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to delete notifications');
      }

      const notificationRef = doc(db, this.collection, notificationId);
      const notificationSnap = await getDoc(notificationRef);

      if (!notificationSnap.exists()) {
        throw new Error('Notification not found');
      }

      const notification = notificationSnap.data() as Notification;

      // Users can only delete their own notifications
      if (notification.userId !== currentUser.id && !AuthService.hasPermission(currentUser, Permission.MANAGE_SYSTEM)) {
        throw new Error('You can only delete your own notifications');
      }

      await deleteDoc(notificationRef);
    } catch (error: any) {
      throw new Error(`Failed to delete notification: ${error.message || error}`);
    }
  }

  // Get unread count for a user
  static async getUnreadCount(userId: string): Promise<number> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to get unread count');
      }

      // Users can only get their own unread count
      if (currentUser.id !== userId && !AuthService.hasPermission(currentUser, Permission.MANAGE_SYSTEM)) {
        throw new Error('You can only view your own notification count');
      }

      const q = query(
        collection(db, this.collection),
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error: any) {
      throw new Error(`Failed to get unread count: ${error.message || error}`);
    }
  }

  // Subscribe to notifications for real-time updates
  static subscribeToNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    // Note: This is a simplified version. In production, you'd want to check permissions here too.
    const q = query(
      collection(db, this.collection),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Notification[];
        callback(notifications);
      },
      (error) => {
        if (onError) {
          onError(error);
        } else {
          console.error('Error subscribing to notifications:', error);
        }
      }
    );
  }

  // Subscribe to unread count for real-time updates
  static subscribeToUnreadCount(
    userId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.collection),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.size);
      },
      (error) => {
        if (onError) {
          onError(error);
        } else {
          console.error('Error subscribing to unread count:', error);
        }
      }
    );
  }
}

